import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles.css';

export default function ContactModal({ open, onClose, onSubmit }) {
  const firstFieldRef = useRef(null);
  const formRef = useRef(null);   // keep a stable handle to the form
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  // states: 'idle' | 'sending' | 'success' | 'error'

  // Close on ESC & focus first field when opened
  useEffect(() => {
    if (!open) return;
    // focus on the first field
    const id = setTimeout(() => firstFieldRef.current?.focus(), 0);

    // lock background scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // close on ESC
    const onKey = (e) => e.key === 'Escape' && !sending && onClose();
    window.addEventListener('keydown', onKey);

    // focus name field after mount
    return () => {
      clearTimeout(id);
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      setSending(false); //resets when closing
      setStatus({ state: 'idle', message: '' });
    };
  }, [open, onClose, sending]);

  if (!open) return null;

  const handleSubmit = async (e) => {
  e.preventDefault();   //stop native from submit/navigation
  if (sending) return;  // guard against double clicks

  const formE1 = formRef.current;   // use ref, not e.currentTarget after await

  const data = Object.fromEntries(new FormData(e.currentTarget).entries());

  try {
    setSending(true);
    setStatus({ state: 'sending', message: 'Sending…' });

    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  //   if (res.ok) {
  //     alert('Thanks! We’ll be in touch.');
  //     e.currentTarget.reset();
  //     onClose();
  //   } else {
  //     alert('Sorry—something went wrong.');
  //   }
  // } catch {
  //   alert('Network error. Please try again.');

    if (!res.ok) {
      const text = await res.text().catch(()=> 'Server error');
      setStatus({ state: 'error', message: text || 'Something went wrong.' });
      return; // keep modal open so user can fix and re-submit
    }

    // optional callback to parent
    onSubmit?.(data);
    setStatus({ state: 'success', message: 'Thanks! We’ll be in touch.' });

    formE1?.reset();
    setTimeout(()=> onClose(), 2000);   // auto close after success
  } catch {
    // ONly show one error. not after success.
    setStatus({ state: 'error', message: 'Network error. Please try again.'});
  } finally {
    setSending(false);
  }
};

 return createPortal(
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => !sending && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={() => !sending && onClose()} aria-label="Close">×</button>
        <h2 className="modal-title">Contact Us</h2>

        <form ref={formRef} onSubmit={handleSubmit} noValidate>
          <label>
            Name
            <input ref={firstFieldRef} name="name" required placeholder="Your full name" />
          </label>
          <label>
            Phone
            <input name="phone" inputMode="tel" placeholder="(optional)" />
          </label>
          <label>
            Email
            <input name="email" type="email" required placeholder="you@example.com" />
          </label>
          <label>
            Message
            <textarea name="message" rows="5" required placeholder="How can we help?" />
          </label>
          <button type="submit" disabled={sending}>
            {sending ? 'Sending...' : 'Send'}
          </button>

          {/* inline status */}
          {status.state !== 'idle' && (
            <p
              className={`form-status ${status.state}`}
              role={status.state === 'error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              {status.message}
            </p>
          )}
          {/* Honeypot (invisible to humans) */}
          <input name="company" autoComplete="off"
          style={{ position:'absolute', left:-9999 }} tabIndex={-1} />

          {/* Human dwell-time */}
          <input type="hidden" name="startedAt" value={Date.now()} />

          {/* If you add reCAPTCHA v3 later, set a state and include it too:
          <input type="hidden" name="captchaToken" value={captchaToken} />
          */}
        </form>
      </div>
    </div>,
    document.body
  );
}
